import { useEffect, useState } from 'react';
import { api, AdminVoucher } from '../lib/api';
import { toast } from 'sonner';

function Vouchers() {
  const [vouchers, setVouchers] = useState<AdminVoucher[]>([]);
  const [loading, setLoading] = useState(true);

  const [code, setCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [productIds, setProductIds] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);

  const loadVouchers = async () => {
    setLoading(true);
    try {
      const result = await api.vouchers.list();
      setVouchers(result.vouchers);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVouchers();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const percent = parseInt(discountPercent, 10);
    if (!code.trim()) {
      toast.error('Voucher code is required');
      return;
    }
    if (Number.isNaN(percent) || percent < 1 || percent > 100) {
      toast.error('Discount percent must be between 1 and 100');
      return;
    }
    setCreating(true);
    try {
      const productIdList = productIds
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter((v) => !Number.isNaN(v));
      const created = await api.vouchers.create({
        code: code.trim(),
        discount_percent: percent,
        product_ids: productIdList.length > 0 ? productIdList : null,
        max_uses: maxUses.trim() ? parseInt(maxUses, 10) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      toast.success(`Voucher ${created.code} created`);
      setCode('');
      setDiscountPercent('');
      setProductIds('');
      setMaxUses('');
      setExpiresAt('');
      await loadVouchers();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (voucher: AdminVoucher) => {
    try {
      const updated = await api.vouchers.update(voucher.id, { is_active: !voucher.is_active });
      setVouchers((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
      toast.success(
        updated.is_active
          ? `Voucher ${updated.code} activated`
          : `Voucher ${updated.code} deactivated`
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="container">
      <h1 className="page-title">Vouchers</h1>
      <p className="page-subtitle">
        Create product-targeted percentage discount codes. Usage counts when a payment succeeds.
      </p>

      <form className="form" onSubmit={handleCreate} style={{ maxWidth: '860px' }}>
        <div className="panel">
          <h2>Create voucher</h2>
          <div className="form-row">
            <label htmlFor="voucher-code">Code</label>
            <input
              id="voucher-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="SUMMER20"
            />
          </div>
          <div className="form-row">
            <label htmlFor="voucher-percent">Discount percent (1 – 100)</label>
            <input
              id="voucher-percent"
              type="number"
              min="1"
              max="100"
              step="1"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              placeholder="20"
            />
          </div>
          <div className="form-row">
            <label htmlFor="voucher-products">Product IDs (comma-separated, optional)</label>
            <input
              id="voucher-products"
              type="text"
              value={productIds}
              onChange={(e) => setProductIds(e.target.value)}
              placeholder="3,7,12"
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              Only these products are eligible. Leave empty to create a voucher that applies to no
              products.
            </span>
          </div>
          <div className="form-row">
            <label htmlFor="voucher-uses">Max uses (optional)</label>
            <input
              id="voucher-uses"
              type="number"
              min="1"
              step="1"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
            />
          </div>
          <div className="form-row">
            <label htmlFor="voucher-expires">Expires at (optional)</label>
            <input
              id="voucher-expires"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary btn--lg" disabled={creating}>
              {creating ? 'Creating…' : 'Create Voucher'}
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="panel">
          <p>Loading vouchers…</p>
        </div>
      ) : vouchers.length === 0 ? (
        <div className="panel">
          <p>No vouchers yet.</p>
        </div>
      ) : (
        <div className="panel">
          <h2>Existing vouchers</h2>
          <table className="table table--admin">
            <thead>
              <tr>
                <th>Code</th>
                <th>%</th>
                <th>Products</th>
                <th>Uses</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map((voucher) => (
                <tr key={voucher.id}>
                  <td>
                    <strong>{voucher.code}</strong>
                  </td>
                  <td>{voucher.discount_percent}%</td>
                  <td>
                    {voucher.product_ids && voucher.product_ids.length > 0
                      ? voucher.product_ids.join(', ')
                      : '—'}
                  </td>
                  <td>
                    {voucher.usage_count}
                    {voucher.max_uses != null ? ` / ${voucher.max_uses}` : ''}
                  </td>
                  <td>
                    {voucher.expires_at ? new Date(voucher.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td>{voucher.is_active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => toggleActive(voucher)}
                    >
                      {voucher.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Vouchers;
